import React, { useState } from "react";
import moment from "moment";
import * as XLSX from "xlsx";
import { jsPDF } from "jspdf";
import { autoTable } from "jspdf-autotable";
import { MdDownload } from "react-icons/md";
import toast from "react-hot-toast";
import TransactionHistoryServices from "../../../services/TransactionHistoryServices";

const BalanceHistoryDownload = ({
  from,
  to,
  userId,
  q,
  paymentType,
  userName = "",
  disabled,
}) => {
  const [downloading, setDownloading] = useState(null);
  const txApi = new TransactionHistoryServices();

  const fetchAllData = async () => {
    return await txApi.getBalanceHistoryForExport({
      q,
      from,
      to,
      userId,
      paymentType,
    });
  };

  const downloadExcel = async () => {
    setDownloading("excel");
    try {
      const data = await fetchAllData();
      if (!data?.length) {
        toast.error("No balance history data to download");
        return;
      }
      const rows = data.map((r, idx) => ({
        "Sr. No.": idx + 1,
        "User Name": r.user_name || "-",
        Date: r.created_at
          ? moment.utc(r.created_at).local().format("DD/MM/YYYY hh:mm A")
          : "—",
        Type: r.reference_type || "-",
        "Txn Type": r.txn_type || "-",
        Credit: r.txn_type === "CREDIT" ? r.amount : "-",
        Debit: r.txn_type === "DEBIT" ? r.amount : "-",
        "Balance Before": r.balance_before ?? "-",
        "Balance After": r.balance_after ?? "-",
        Details: r.remark || "-",
      }));
      const ws = XLSX.utils.json_to_sheet(rows);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Balance History");
      const safeName = (userName || "all").replace(/[^a-zA-Z0-9_-]/g, "_");
      const fileName = `balance_history_${safeName}_${from}_to_${to}.xlsx`;
      XLSX.writeFile(wb, fileName);
      toast.success("Excel downloaded");
    } catch (err) {
      console.error("Excel download error:", err);
      toast.error(err?.message || "Failed to download Excel");
    } finally {
      setDownloading(null);
    }
  };

  const downloadPDF = async () => {
    setDownloading("pdf");
    try {
      const data = await fetchAllData();
      if (!data?.length) {
        toast.error("No balance history data to download");
        return;
      }
      const doc = new jsPDF({ orientation: "portrait", unit: "mm" });
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const bottomMargin = 20;
      const maxY = pageHeight - bottomMargin;
      let y = 15;

      doc.setFontSize(14);
      doc.text("Balance History", pageWidth / 2, y, { align: "center" });
      y += 8;
      doc.setFontSize(10);
      doc.text(
        `User: ${userName || "All"} | From: ${from} to To: ${to}`,
        pageWidth / 2,
        y,
        { align: "center" },
      );
      y += 14;

      const tableData = data.map((r, idx) => [
        idx + 1,
        r.user_name || "-",
        r.created_at
          ? moment.utc(r.created_at).local().format("DD/MM/YYYY hh:mm A")
          : "—",
        r.reference_type || "-",
        r.txn_type === "CREDIT" ? r.amount : "-",
        r.txn_type === "DEBIT" ? r.amount : "-",
        r.balance_after ?? "-",
        (r.remark || "-").substring(0, 60),
      ]);

      autoTable(doc, {
        startY: y,
        head: [
          [
            "Sr.",
            "User",
            "Date",
            "Type",
            "Credit",
            "Debit",
            "Balance",
            "Details",
          ],
        ],
        body: tableData,
        theme: "grid",
        margin: { left: 14 },
        tableWidth: pageWidth - 28,
        styles: { fontSize: 8 },
        columnStyles: {
          0: { cellWidth: 10 },
          1: { cellWidth: 25 },
          2: { cellWidth: 28 },
          3: { cellWidth: 22 },
          4: { cellWidth: 18 },
          5: { cellWidth: 18 },
          6: { cellWidth: 20 },
          7: { cellWidth: "auto" },
        },
      });
      y = doc.lastAutoTable.finalY + 10;

      const safeName = (userName || "all").replace(/[^a-zA-Z0-9_-]/g, "_");
      const fileName = `balance_history_${safeName}_${from}_to_${to}.pdf`;
      doc.save(fileName);
      toast.success("PDF downloaded");
    } catch (err) {
      console.error("PDF download error:", err);
      toast.error(err?.message || "Failed to download PDF");
    } finally {
      setDownloading(null);
    }
  };

  return (
    <div className="report-download-wrap">
      <div className="report-download-hover-wrap">
        <div className="report-download-trigger">
          <MdDownload size={20} />
        </div>
        <div className="report-download-dropdown">
          <button
            type="button"
            className="report-download-option"
            onClick={downloadExcel}
            disabled={disabled}
            title="Download Excel"
          >
            <MdDownload size={16} />
            <span>{downloading === "excel" ? "Downloading..." : "Excel"}</span>
          </button>
          <button
            type="button"
            className="report-download-option"
            onClick={downloadPDF}
            disabled={disabled}
            title="Download PDF"
          >
            <MdDownload size={16} />
            <span>{downloading === "pdf" ? "Downloading..." : "PDF"}</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default BalanceHistoryDownload;
